import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';

import { $sceneBindings } from '../../state/appState';
import { $activeSceneId } from '../../state/sceneNavigation';
import { getSplatSceneConfig } from '../../scene/splatRegistry';
import { defaultCameraConfig } from '../../cameraConfig';
import {
  FLYOVER_PRESETS,
  round3,
  roundV3,
  type FlyoverPreset,
  type FlyoverPresetInput,
} from '../../scene/camera/flyoverPresets';
import type { CameraFlyoverConfig, Vector3Like } from '../../scene/sceneConfig';
import styles from './FlyoverTuner.module.css';

/**
 * Flyover authoring panel, gated by its own `?flight` URL param (NOT
 * `?dev`). Build YOUR OWN flyover: capture the current camera pose as
 * waypoints, edit durations, delete rows, Replay the draft, Copy the
 * config. The draft is session-state only — entering a scene always
 * starts blank (deliberate: no persistence, "Copy config" is the way to
 * keep a result).
 */
const DEFAULT_WAYPOINT_DURATION = 2;
const DEFAULT_ORBIT_ANGLE_DEG = 180;
const DEFAULT_ORBIT_DURATION = 4;
/** Two poses closer than this are "the same point" (spline NaN guard). */
const DUPLICATE_POSE_EPSILON = 0.002;
/** Below these an orbit degenerates into coincident spline points (NaN). */
const MIN_ORBIT_RADIUS = 0.05;
const MIN_ORBIT_ANGLE_DEG = 5;
const MIN_SEGMENT_DURATION = 0.1;

/**
 * Panel-local row identity. `key={index}` is NOT enough here: rows carry
 * uncontrolled duration inputs, so on delete React would reuse DOM rows
 * under shifted indexes and typed values would "migrate" into neighbor
 * rows — looking exactly like "the waypoint didn't really get deleted".
 * `__id` never leaves the panel: it is stripped from the Copy-config
 * snippet (field-by-field builder) and from the config passed to `play()`.
 */
type DraftSegment = CameraFlyoverConfig['segments'][number] & { __id: number };

type DraftConfig = Omit<CameraFlyoverConfig, 'segments'> & { segments: DraftSegment[] };

type Draft = {
  sceneId: string;
  config: DraftConfig;
};

let nextRowId = 1;

const withRowId = (segment: CameraFlyoverConfig['segments'][number]): DraftSegment => ({
  ...segment,
  __id: nextRowId++,
});

const stripRowId = (key: string, value: unknown): unknown =>
  key === '__id' ? undefined : value;

const fmtV3 = (v: Vector3Like): string => `${v.x}, ${v.y}, ${v.z}`;

const tsV3 = (v: Vector3Like): string => `{ x: ${v.x}, y: ${v.y}, z: ${v.z} }`;

/** Paste-ready TS block for the scene config's `camera.flyover` field. */
const buildConfigSnippet = (config: DraftConfig): string => {
  const lines: string[] = ['flyover: {'];
  if (config.delay) lines.push(`  delay: ${config.delay},`);
  if (config.mode === 'segments') lines.push(`  mode: 'segments',`);
  lines.push('  segments: [');
  for (const seg of config.segments) {
    lines.push(
      seg.type === 'waypoint'
        ? `    { type: 'waypoint', position: ${tsV3(seg.position)}, target: ${tsV3(seg.target)}, duration: ${seg.duration} },`
        : `    { type: 'orbit', center: ${tsV3(seg.center)}, radius: ${seg.radius}, height: ${seg.height}, angleDeg: ${seg.angleDeg}, duration: ${seg.duration} },`,
    );
  }
  lines.push('  ],', '},');
  return lines.join('\n');
};

/** Deep-copy WITHOUT panel-local `__id`s — what leaves the panel is a pure config. */
const cloneConfig = (config: DraftConfig): CameraFlyoverConfig =>
  JSON.parse(JSON.stringify(config, stripRowId)) as CameraFlyoverConfig;

const isNearV3 = (a: Vector3Like, b: Vector3Like): boolean =>
  Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) < DUPLICATE_POSE_EPSILON;

/**
 * Uncontrolled numeric field committed on blur/Enter. Controlled number
 * inputs eat intermediate typing states like "2."; call sites pass a
 * `key` of `<rowId>-<value>` so external value changes remount it with a
 * fresh `defaultValue` while typing never does.
 */
const NumField = ({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number;
  onCommit: (value: number) => void;
}) => (
  <label className={styles.duration}>
    <span>{label}</span>
    <input
      className={styles.durationInput}
      type="number"
      step="0.1"
      defaultValue={value}
      onBlur={(event) => {
        const next = parseFloat(event.target.value);
        if (Number.isFinite(next) && next !== value) onCommit(next);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
    />
  </label>
);

export const FlyoverTuner = () => {
  const { bindings, sceneId } = useUnit({
    bindings: $sceneBindings,
    sceneId: $activeSceneId,
  });

  const [collapsed, setCollapsed] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  const sceneConfig = useMemo(
    () => (sceneId ? getSplatSceneConfig(sceneId) ?? null : null),
    [sceneId],
  );
  // The flight's fixed start: the scene's entry pose (`previewTourEnd`).
  // Shown as a non-deletable row so the visible list IS the flight —
  // replay and the real scene-load flyover both begin here. Not part of
  // the draft (it lives in the scene config, not in `flyover.segments`).
  const startPose = sceneConfig?.camera.previewTourEnd ?? null;
  // The scene's CURRENT production flyover (if any) — importable into the
  // draft so existing flights can be tweaked instead of rebuilt.
  const sceneFlyover = sceneConfig?.camera.flyover ?? null;

  const presetInput = useMemo((): FlyoverPresetInput | null => {
    if (!startPose) return null;
    return {
      orbitCenter: sceneConfig?.controls?.orbitCenter ?? startPose.target,
      tourEndPosition: startPose.position,
      tourEndTarget: startPose.target,
    };
  }, [sceneConfig, startPose]);

  // Camera limits for final-pose validation — resolved the same way the
  // scene does it: config overrides on top of `defaultCameraConfig`.
  const clamps = useMemo(() => {
    const overrides = sceneConfig?.controls;
    return {
      clampMinHeight: overrides?.clampMinHeight ?? defaultCameraConfig.clampMinHeight,
      clampMaxHeight: overrides?.clampMaxHeight ?? defaultCameraConfig.clampMaxHeight,
      orbitMinDistance: overrides?.orbitMinDistance ?? defaultCameraConfig.orbitMinDistance,
      orbitMaxDistance: overrides?.orbitMaxDistance ?? defaultCameraConfig.orbitMaxDistance,
    };
  }, [sceneConfig]);

  // Fresh blank draft on every scene entry — deliberately NO persistence
  // ("Copy config" is the way to keep a result).
  useEffect(() => {
    if (!sceneId) return;
    setDraft((prev) =>
      prev?.sceneId === sceneId ? prev : { sceneId, config: { segments: [] } },
    );
  }, [sceneId]);

  const updateSegments = useCallback(
    (mutate: (segments: DraftSegment[]) => DraftSegment[]) => {
      setDraft((prev) =>
        prev
          ? { ...prev, config: { ...prev.config, segments: mutate(prev.config.segments) } }
          : prev,
      );
    },
    [],
  );

  const handleAddWaypoint = useCallback(() => {
    if (!bindings) return;
    const pose = bindings.getCurrentCameraPose();
    const position = roundV3(pose.position);
    const target = roundV3(pose.target);
    updateSegments((segments) => {
      // Ignore an accidental duplicate (double-click on Add / camera not
      // moved since the last capture) — adjacent coincident points would
      // also NaN the spline.
      const last = segments[segments.length - 1];
      if (
        last &&
        last.type === 'waypoint' &&
        isNearV3(last.position, position) &&
        isNearV3(last.target, target)
      ) {
        return segments;
      }
      return [
        ...segments,
        withRowId({
          type: 'waypoint',
          position,
          target,
          duration: DEFAULT_WAYPOINT_DURATION,
        }),
      ];
    });
  }, [bindings, updateSegments]);

  const handleAddOrbit = useCallback(() => {
    if (!bindings) return;
    // Orbit derived from the current framing: pivot = what the camera looks
    // at, radius/height = where the camera stands relative to it. Only the
    // arc angle is typed by hand (defaults to a half-circle).
    const pose = bindings.getCurrentCameraPose();
    const center = roundV3(pose.target);
    const radius = round3(
      Math.hypot(pose.position.x - center.x, pose.position.z - center.z),
    );
    if (radius < MIN_ORBIT_RADIUS) {
      window.alert(
        'Orbit needs a horizontal offset from the target — move the camera off the vertical axis first.',
      );
      return;
    }
    updateSegments((segments) => [
      ...segments,
      withRowId({
        type: 'orbit',
        center,
        radius,
        height: round3(pose.position.y - center.y),
        angleDeg: DEFAULT_ORBIT_ANGLE_DEG,
        duration: DEFAULT_ORBIT_DURATION,
      }),
    ]);
  }, [bindings, updateSegments]);

  const patchSegment = useCallback(
    (id: number, patch: Partial<CameraFlyoverConfig['segments'][number]>) => {
      updateSegments((segments) =>
        segments.map((seg) => (seg.__id === id ? ({ ...seg, ...patch } as DraftSegment) : seg)),
      );
    },
    [updateSegments],
  );

  const handleRemove = useCallback(
    (id: number) => {
      updateSegments((segments) => segments.filter((seg) => seg.__id !== id));
    },
    [updateSegments],
  );

  const handleGoTo = useCallback(
    (segment: DraftSegment) => {
      if (!bindings || segment.type !== 'waypoint') return;
      bindings.flyoverTuner.goToPose({
        position: segment.position,
        target: segment.target,
      });
    },
    [bindings],
  );

  const handleRecapture = useCallback(
    (id: number) => {
      if (!bindings) return;
      const pose = bindings.getCurrentCameraPose();
      const position = roundV3(pose.position);
      const target = roundV3(pose.target);
      updateSegments((segments) => {
        const index = segments.findIndex((seg) => seg.__id === id);
        if (index < 0) return segments;
        // NaN guard: the new pose must not coincide with a NEIGHBOR row —
        // adjacent duplicate spline control points break the CatmullRom.
        const prev = segments[index - 1];
        const next = segments[index + 1];
        if (
          (prev?.type === 'waypoint' && isNearV3(prev.position, position)) ||
          (next?.type === 'waypoint' && isNearV3(next.position, position))
        ) {
          return segments;
        }
        return segments.map((seg, i) => (i === index ? { ...seg, position, target } : seg));
      });
    },
    [bindings, updateSegments],
  );

  const handleMove = useCallback(
    (id: number, direction: -1 | 1) => {
      updateSegments((segments) => {
        const from = segments.findIndex((seg) => seg.__id === id);
        const to = from + direction;
        if (from < 0 || to < 0 || to >= segments.length) return segments;
        const next = [...segments];
        [next[from], next[to]] = [next[to], next[from]];
        return next;
      });
    },
    [updateSegments],
  );

  const handleLoadSceneFlyover = useCallback(() => {
    if (!draft || !sceneFlyover) return;
    if (
      draft.config.segments.length > 0 &&
      !window.confirm('Replace the current draft with the scene’s flyover config?')
    ) {
      return;
    }
    // Deep clone — the draft must never hold references into the scene
    // config's nested position/target objects.
    const copy = JSON.parse(JSON.stringify(sceneFlyover)) as CameraFlyoverConfig;
    setDraft({
      sceneId: draft.sceneId,
      config: { ...copy, segments: copy.segments.map(withRowId) },
    });
  }, [draft, sceneFlyover]);

  const handleApplyPreset = useCallback(
    (preset: FlyoverPreset) => {
      if (!draft || !presetInput) return;
      const segments = preset.build(presetInput);
      if (segments.length === 0) {
        window.alert(
          'Preset unavailable: the start pose is (almost) directly above the orbit center.',
        );
        return;
      }
      if (
        draft.config.segments.length > 0 &&
        !window.confirm('Replace the current draft with the preset?')
      ) {
        return;
      }
      setDraft({
        sceneId: draft.sceneId,
        config: { ...draft.config, segments: segments.map(withRowId) },
      });
    },
    [draft, presetInput],
  );

  // Non-blocking authoring warnings. Clamp violations only snap AFTER the
  // flight (controls are disabled while it plays), so these are hints, not
  // hard errors.
  const warnings = useMemo(() => {
    if (!draft) return [];
    const result: string[] = [];
    const segs = draft.config.segments;
    for (let i = 0; i < segs.length - 1; i++) {
      const a = segs[i];
      const b = segs[i + 1];
      if (a.type === 'waypoint' && b.type === 'waypoint' && isNearV3(a.position, b.position)) {
        result.push(`#${i + 1} and #${i + 2} coincide — breaks the flight (NaN)`);
      }
    }
    const first = segs[0];
    if (first?.type === 'waypoint' && startPose && isNearV3(first.position, startPose.position)) {
      result.push('#1 coincides with the start pose — delete it (breaks the flight at scene load)');
    }
    const last = segs[segs.length - 1];
    if (last) {
      const y = last.type === 'waypoint' ? last.position.y : last.center.y + last.height;
      const distance =
        last.type === 'waypoint'
          ? Math.hypot(
              last.position.x - last.target.x,
              last.position.y - last.target.y,
              last.position.z - last.target.z,
            )
          : Math.hypot(last.radius, last.height);
      if (y > clamps.clampMaxHeight) {
        result.push(
          `final pose y=${round3(y)} above the camera height limit ${clamps.clampMaxHeight} — snap at the end`,
        );
      }
      if (y < clamps.clampMinHeight) {
        result.push(
          `final pose y=${round3(y)} below the camera height limit ${clamps.clampMinHeight} — snap at the end`,
        );
      }
      if (distance > clamps.orbitMaxDistance) {
        result.push(
          `final distance ${round3(distance)} beyond the zoom-out limit ${clamps.orbitMaxDistance} — snap at the end`,
        );
      }
      if (distance < clamps.orbitMinDistance) {
        result.push(
          `final distance ${round3(distance)} inside the zoom-in limit ${clamps.orbitMinDistance} — snap at the end`,
        );
      }
    }
    const total = segs.reduce((sum, seg) => sum + seg.duration, 0);
    if (total > 10) {
      result.push(`flight is ${round3(total)}s — the chat appears only after it finishes`);
    }
    return result;
  }, [draft, clamps, startPose]);

  const handleReplay = useCallback(() => {
    if (!bindings || !draft || playing || draft.config.segments.length === 0) return;
    setPlaying(true);
    // Clone: `CameraFlyover` keeps a live reference to `segments` — panel
    // edits mid-replay must not corrupt the running animation.
    void bindings.flyoverTuner.play(cloneConfig(draft.config)).finally(() => setPlaying(false));
  }, [bindings, draft, playing]);

  const handleStop = useCallback(() => {
    bindings?.flyoverTuner.stop();
  }, [bindings]);

  const handleCopy = useCallback(() => {
    if (!draft) return;
    const snippet = buildConfigSnippet(draft.config);
    console.log(`[FlyoverTuner] ${draft.sceneId} config:\n${snippet}`);
    void navigator.clipboard?.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [draft]);


  // Bindings are published only by splat scenes — the panel hides itself
  // on GLB scenes and between scene mounts.
  if (!bindings || !sceneId || !draft || draft.sceneId !== sceneId) {
    return null;
  }

  const segments = draft.config.segments;
  const totalDuration = round3(segments.reduce((sum, seg) => sum + seg.duration, 0));

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setCollapsed((prev) => !prev)}
      >
        <span>{collapsed ? '▸' : '▾'}</span>
        <span>Flyover — {sceneId}</span>
        <span className={styles.count}>
          {segments.length}
          {totalDuration > 0 ? ` · ${totalDuration}s` : ''}
        </span>
      </button>

      {!collapsed && (
        <div className={styles.body}>
          <div className={styles.topRow}>
            <button type="button" className={styles.footerButton} onClick={handleAddWaypoint}>
              📷 Add waypoint
            </button>
            <button
              type="button"
              className={styles.footerButton}
              onClick={handleAddOrbit}
              title="Orbit around the current camera target: radius/height from the camera pose, angle editable in the row"
            >
              ⭕ Add orbit
            </button>
            <button
              type="button"
              className={styles.footerButton}
              onClick={handleLoadSceneFlyover}
              disabled={!sceneFlyover}
              title={
                sceneFlyover
                  ? 'Import the scene’s current flyover config into the draft'
                  : 'This scene has no flyover config yet'
              }
            >
              ⬇ Load scene flyover
            </button>
          </div>

          <div className={styles.heading}>Presets</div>
          <div className={styles.topRow}>
            {FLYOVER_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={styles.footerButton}
                onClick={() => handleApplyPreset(preset)}
                disabled={!presetInput}
                title="Fills the draft with a ready-made flight (replaces current rows); tweak the fields afterwards"
              >
                {preset.label}
              </button>
            ))}
          </div>

          {startPose && (
            <div className={styles.startRow}>
              <div className={styles.rowInfo}>
                <span className={styles.rowIndex}>start</span>
                <span className={styles.rowCoords}>
                  pos {fmtV3(roundV3(startPose.position))}
                  <br />
                  tgt {fmtV3(roundV3(startPose.target))}
                </span>
              </div>
              <span
                className={styles.startBadge}
                title="Scene entry pose — the flight always begins here (fixed, from the scene config)"
              >
                fixed
              </span>
            </div>
          )}

          {segments.length === 0 ? (
            <div className={styles.hint}>
              Fly the camera to a nice frame, then add waypoints one by one —
              the flight goes from the start pose through them in order.
            </div>
          ) : (
            segments.map((segment, index) => (
                <div key={segment.__id} className={styles.row}>
                  <div className={styles.rowInfo}>
                    <span className={styles.rowIndex}>
                      #{index + 1}
                      {segment.type === 'orbit' ? ' ⭕' : ''}
                    </span>
                    {segment.type === 'waypoint' ? (
                      <span className={styles.rowCoords}>
                        pos {fmtV3(segment.position)}
                        <br />
                        tgt {fmtV3(segment.target)}
                      </span>
                    ) : (
                      <span className={styles.rowCoords}>center {fmtV3(segment.center)}</span>
                    )}
                  </div>
                  {segment.type === 'orbit' && (
                    <div className={styles.rowFields}>
                      <NumField
                        key={`r-${segment.__id}-${segment.radius}`}
                        label="r"
                        value={segment.radius}
                        onCommit={(v) =>
                          patchSegment(segment.__id, {
                            radius: Math.max(round3(v), MIN_ORBIT_RADIUS),
                          })
                        }
                      />
                      <NumField
                        key={`h-${segment.__id}-${segment.height}`}
                        label="h"
                        value={segment.height}
                        onCommit={(v) => patchSegment(segment.__id, { height: round3(v) })}
                      />
                      <NumField
                        key={`a-${segment.__id}-${segment.angleDeg}`}
                        label="∠°"
                        value={segment.angleDeg}
                        onCommit={(v) =>
                          patchSegment(segment.__id, {
                            // Keep the sign (direction), clamp the magnitude
                            // away from the degenerate-arc zone.
                            angleDeg:
                              v >= 0
                                ? Math.max(Math.round(v), MIN_ORBIT_ANGLE_DEG)
                                : Math.min(Math.round(v), -MIN_ORBIT_ANGLE_DEG),
                          })
                        }
                      />
                    </div>
                  )}
                  <div className={styles.rowActions}>
                    {segment.type === 'waypoint' && (
                      <>
                        <button
                          type="button"
                          className={styles.iconButton}
                          title="Fly camera to this waypoint"
                          onClick={() => handleGoTo(segment)}
                        >
                          →
                        </button>
                        <button
                          type="button"
                          className={styles.iconButton}
                          title="Re-capture pose from the current camera (ignored if it would duplicate a neighbor row)"
                          onClick={() => handleRecapture(segment.__id)}
                        >
                          ⟳
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className={styles.iconButton}
                      title="Move up"
                      disabled={index === 0}
                      onClick={() => handleMove(segment.__id, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className={styles.iconButton}
                      title="Move down"
                      disabled={index === segments.length - 1}
                      onClick={() => handleMove(segment.__id, 1)}
                    >
                      ↓
                    </button>
                    <NumField
                      key={`d-${segment.__id}-${segment.duration}`}
                      label="s"
                      value={segment.duration}
                      onCommit={(v) =>
                        patchSegment(segment.__id, {
                          duration: Math.max(round3(v), MIN_SEGMENT_DURATION),
                        })
                      }
                    />
                    <button
                      type="button"
                      className={styles.removeButton}
                      title="Delete waypoint"
                      onClick={() => handleRemove(segment.__id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ),
            )
          )}

          <div className={styles.configRow}>
            <NumField
              key={`delay-${draft.config.delay ?? 0}`}
              label="delay s"
              value={draft.config.delay ?? 0}
              onCommit={(v) =>
                setDraft((prev) =>
                  prev
                    ? {
                        ...prev,
                        config: {
                          ...prev.config,
                          // 0 → omit the field so Copy config stays minimal
                          delay: v > 0 ? round3(v) : undefined,
                        },
                      }
                    : prev,
                )
              }
            />
          </div>

          {warnings.map((warning) => (
            <div key={warning} className={styles.warning}>
              ⚠ {warning}
            </div>
          ))}

          <div className={styles.footer}>
            {playing ? (
              <button type="button" className={styles.footerButton} onClick={handleStop}>
                ⏹ Stop
              </button>
            ) : (
              <button
                type="button"
                className={styles.footerButton}
                onClick={handleReplay}
                disabled={segments.length === 0}
                title="Snap to waypoint #1 and fly through the rest of the draft"
              >
                ▶ Replay
              </button>
            )}
            <button
              type="button"
              className={styles.footerButton}
              onClick={handleCopy}
              disabled={segments.length === 0}
              title="Copy a paste-ready `flyover: {…}` block for the scene config"
            >
              {copied ? 'Copied!' : 'Copy config'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
